<script setup lang="ts">
import 'vue3-carousel/dist/carousel.css'
import { onMounted, ref } from 'vue'
import { Carousel, Slide } from 'vue3-carousel'
import { getPublicBusinessRequestList } from '@/services/modules/BusinessRequestService'
import { IPublicBusinessRequest } from '@/models/interfaces/interfaces'
// @ts-ignore
import i18n from '@/localization/i18n'

const t = i18n.global.t

const requests = ref<IPublicBusinessRequest[]>([])
const loading = ref(false)
const error = ref(false)

const demoRequests: IPublicBusinessRequest[] = [
  {
    id: 1,
    title: 'Besoin d’un plombier pour fuite rapide',
    description: 'Urgence plomberie dans la cuisine, fuite sous l’évier. Intervention rapide demandée cette semaine.',
    town: 'Paris',
    zipcode: '75011',
    publish_date: '2026-06-30T10:15:00Z'
  },
  {
    id: 2,
    title: 'Peinture d’un appartement de 3 pièces',
    description: 'Appartement à rénover avec préparation des murs, peinture intérieure et finition soignée.',
    town: 'Lyon',
    zipcode: '69002',
    publish_date: '2026-06-29T18:40:00Z'
  },
  {
    id: 3,
    title: 'Installation de climatisation réversible',
    description: 'Besoin d’un professionnel pour une installation discrète avec conseil sur le dimensionnement.',
    town: 'Marseille',
    zipcode: '13003',
    publish_date: '2026-06-28T14:20:00Z'
  }
]

const activeSlide = ref(0)
const itemsToShow = ref(3)
const isClient = ref(false)

onMounted(() => { isClient.value = true })

const breakpoints = {
  0: {
    itemsToShow: 1
  },
  768: {
    itemsToShow: 2
  },
  1200: {
    itemsToShow: 3
  }
}

const stripHtml = (value?: string | null) => {
  if (!value) return ''
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

const getPreviewText = (request: IPublicBusinessRequest) => {
  const raw = stripHtml(request.description || '')
  if (!raw) return t('w_txt_public_requests_preview_placeholder')
  return raw
}

const fetchLatestRequests = async () => {
  loading.value = true
  error.value = false

  try {
    const items = await getPublicBusinessRequestList()
    requests.value = items
  } catch (e) {
    console.error('Unable to load public latest requests', e)
    requests.value = demoRequests
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchLatestRequests()
})
</script>

<template>
  <section class="pt-5 pb-0 bg-orange">
    <div class="container">
      <div class="text-center mb-4">
        <h2 class="fw-semibold mb-2">{{ t('w_txt_public_requests_title') }}</h2>
        <p class="text-muted mb-0">
          {{ t('w_txt_public_requests_subtitle') }}
        </p>
      </div>
    </div>
  </section>
  <section class="p-0 bg-orange">
    <div class="container p-0">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status" :aria-label="t('w_txt_public_requests_loading')"></div>
      </div>

      <div v-else-if="error" class="alert alert-warning text-center">
        {{ t('w_txt_public_requests_error') }}
      </div>

      <div v-else-if="requests.length === 0" class="text-center text-muted py-4">
        {{ t('w_txt_public_requests_empty') }}
      </div>

      <div v-else >
        <Carousel v-model="activeSlide" :itemsToShow="itemsToShow" :wrapAround="true" snapAlign="start"
          :mouseDrag="true" :touchDrag="true" :pauseAutoplayOnHover="true" :autoplay="isClient ? 5000 : 0" :transition="700"
          :breakpoints="breakpoints" class="public-requests-carousel py-5">
          <Slide v-for="request in requests" :key="request.id">
            <div class="request-card card h-100 shadow-sm rounded-4 border border-secondary-subtle p-4 m-2">
              <h3 class="h6 fw-semibold mb-2">{{ request.title }}</h3>
              <div v-if="request.town || request.zipcode" class="text-muted small mb-3">
                {{ request.town }}{{ request.zipcode ? ` (${request.zipcode})` : '' }}
              </div>
              <p class="text-muted mb-0 request-card-description">{{ getPreviewText(request) }}</p>
            </div>
          </Slide>
        </Carousel>

        <div class="text-center pb-5 px-4">
          <a class="btn  bg-dark-blue text-white px-5" href="/ps/home/" target="_blank" rel="noopener noreferrer">
            {{ t('w_txt_public_requests_cta') }}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bg-dark-blue {
  background-color: #233648;
}
.bg-orange {
  background-color: #FEB23C;
}

.public-requests-carousel {
  margin: 0 -0.75rem;
}

.request-card {
  /* min-height: 240px; */
}

.request-card h3 {
  font-size: 1rem;
}

.request-card p {
  font-size: 0.95rem;
  line-height: 1.5;
}

.request-card-description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: calc(1.5em * 3);
}

@media (max-width: 767px) {
  .request-card {
    min-height: auto;
  }
  .public-requests-carousel {
    margin: 0 1rem;
  }
}
</style>
